import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useFaqsPage, useUpdateFaqsPage } from '../hooks/useCmsPages';
import Swal from 'sweetalert2';

const FAQs = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    data: []
  });
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Fetch FAQs data using React Query
  const {
    data: faqsData,
    isLoading,
    error,
  } = useFaqsPage();

  // Update FAQs mutation
  const updateFaqsMutation = useUpdateFaqsPage();

  // Initialize edited data when FAQs data is loaded
  React.useEffect(() => {
    if (faqsData && !isEditing) {
      setEditedData(faqsData);
    }
  }, [faqsData, isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedData({ ...faqsData });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({ ...faqsData });
  };

  const handleSave = async () => {
    try {
      // Filter out empty items and validate
      const validItems = editedData.data.filter(item => 
        item.title?.en?.trim() || item.text?.en?.trim()
      );

      if (validItems.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Warning',
          text: 'Please add at least one FAQ item with content.',
        });
        return;
      }

      // Set Arabic content to match English content by default
      const processedItems = validItems.map(item => ({
        title: {
          en: item.title?.en || '',
          ar: item.title?.ar || item.title?.en || ''
        },
        text: {
          en: item.text?.en || '',
          ar: item.text?.en || '' // Always default Arabic text to English text
        }
      }));

      const dataToSend = {
        data: processedItems
      };

      await updateFaqsMutation.mutateAsync(dataToSend);
      setIsEditing(false);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'FAQs updated successfully!',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error updating FAQs:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update FAQs',
      });
    }
  };

  const handleTitleChange = (index, lang, value) => {
    setEditedData(prev => {
      const newData = { ...prev };
      if (!newData.data[index]) {
        newData.data[index] = { 
          title: { en: '', ar: '' }, 
          text: { en: '', ar: '' } 
        };
      }
      newData.data[index] = {
        ...newData.data[index],
        title: {
          ...newData.data[index].title,
          [lang]: value
        }
      };
      return newData;
    });
  };

  const handleTextChange = (index, value) => {
    setEditedData(prev => {
      const newData = { ...prev };
      if (!newData.data[index]) {
        newData.data[index] = { 
          title: { en: '', ar: '' }, 
          text: { en: '', ar: '' } 
        };
      }
      newData.data[index] = {
        ...newData.data[index],
        text: {
          en: value,
          ar: value // Auto-set Arabic to same as English
        }
      };
      return newData;
    });
  };

  const addNewFaq = () => {
    setEditedData(prev => ({
      ...prev,
      data: [
        ...prev.data,
        { title: { en: '', ar: '' }, text: { en: '', ar: '' } }
      ]
    }));
  };

  const removeFaq = (index) => {
    setEditedData(prev => ({
      ...prev,
      data: prev.data.filter((_, i) => i !== index)
    }));
  };

  const toggleAccordion = (index) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </Layout>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader
            title="Frequently Asked Questions"
            description="Manage FAQs content"
          />
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{error.message}</div>
          </div>
        </div>
      </Layout>
    );
  }

  // Convert data to array for display
  const displayData = Array.isArray(faqsData?.data) ? faqsData.data : [faqsData?.data];
  console.log(displayData);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="Frequently Asked Questions"
          description="Manage FAQs content"
          user={user}
        />

        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Content Management</h2>
              <div className="flex space-x-3">
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md transition duration-200"
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition duration-200"
                      disabled={updateFaqsMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={updateFaqsMutation.isPending}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition duration-200 disabled:opacity-50"
                    >
                      {updateFaqsMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-6">
            {!isEditing ? (
              // Display Mode - Accordions
              <div className="space-y-4">
                {displayData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No FAQ items found. Click Edit to add some.
                  </div>
                ) : (
                  displayData?.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleAccordion(index)}
                        className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {item?.title?.en || 'No title set'}
                          </div>
                        </div>
                        {expandedItems.has(index) ? (
                          <FiChevronDown className="h-5 w-5 text-gray-500" />
                        ) : (
                          <FiChevronRight className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      
                      {expandedItems.has(index) && (
                        <div className="px-6 py-4 bg-white border-t border-gray-200">
                          <div className="space-y-4">
                            <div>
                              <div 
                                className="prose max-w-none"
                                dangerouslySetInnerHTML={{ __html: item?.text?.en || 'No content set' }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            ) : (
              // Edit Mode - Accordions
              <div className="space-y-4">
                {editedData.data?.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item?.title?.en || ''}
                          onChange={(e) => handleTitleChange(index, 'en', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Enter title"
                        />
                      </div>
                      <button
                        onClick={() => removeFaq(index)}
                        className="ml-4 text-red-600 hover:text-red-800 p-2"
                        title="Remove FAQ"
                      >
                        <FiTrash2 className="h-5 w-5" />
                      </button>
                    </div>
                    
                    <div className="px-6 py-4 bg-white border-t border-gray-200">
                      <div className="space-y-4">
                        {/* English Content */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content
                          </label>
                          <textarea
                            value={item?.text?.en || ''}
                            onChange={(e) => handleTextChange(index, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            rows="4"
                            placeholder="Enter content"
                          />
                        </div>

                      </div>
                    </div>
                  </div>
                ))}

                {/* Add New FAQ Button */}
                <div className="flex justify-center pt-4">
                  <button
                    onClick={addNewFaq}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md transition duration-200 flex items-center gap-2"
                  >
                    <FiPlus className="h-5 w-5" />
                    Add New FAQ
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FAQs; 